// Digital ID Cards Data Manager - MySQL Database Integration
import apiService from '../services/apiService.js';


export const initializeData = async () => {
  try {
    await apiService.initializeDatabase();
    console.log('Database initialized successfully');
  } catch (error) {
    console.error('Error initializing database:', error);
    throw error;
  }
};
export const getUsers = async () => {
  try {
    return await apiService.getUsers();
  } catch (error) {
    console.error('Error fetching users:', error);
    return [];
  }
};

export const getUserById = async (id) => {
  try {
    return await apiService.getUserById(id);
  } catch (error) {
    console.error('Error fetching user:', error);
    return null;
  }
};

export const createUser = async (userData) => {
  try {
    return await apiService.createUser(userData);
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

export const updateUser = async (id, userData) => {
  try {
    const result = await apiService.updateUser(id, userData);
    return result;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

export const deleteUser = async (id) => {
  try {
    await apiService.deleteUser(id);
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

export const authenticateUser = async (username, password) => {
  try {
    return await apiService.authenticateUser(username, password);
  } catch (error) {
    console.error('Error authenticating user:', error);
    return null;
  }
};

// Digital Cards operations - now using MySQL
export const getDigitalCards = async () => {
  try {
    return await apiService.getDigitalCards();
  } catch (error) {
    console.error('Error fetching cards:', error);
    return [];
  }
};

export const getDigitalCardById = async (id) => {
  try {
    return await apiService.getDigitalCardById(id);
  } catch (error) {
    console.error('Error fetching card:', error);
    return null;
  }
};

// Get card by short code (decode base36 to get ID)
export const getDigitalCardByShortCode = async (shortCode) => {
  try {
    // Short code is just base36 encoded ID (no random chars anymore)
    const cardId = parseInt(shortCode, 36); // Convert base36 to decimal
    
    if (!cardId || isNaN(cardId)) {
      console.error('Invalid short code:', shortCode);
      return null;
    }
    
    console.log('📌 Decoded short code:', shortCode, '→ Card ID:', cardId);
    
    // Fetch card by ID
    return await apiService.getDigitalCardById(cardId);
  } catch (error) {
    console.error('Error fetching card by short code:', error);
    return null;
  }
};

export const createDigitalCard = async (cardData, createdBy) => {
  try {
    return await apiService.createDigitalCard(cardData, createdBy);
  } catch (error) {
    console.error('Error creating card:', error);
    throw error;
  }
};

export const updateDigitalCard = async (id, cardData) => {
  try {
    const result = await apiService.updateDigitalCard(id, cardData);   
    if (!result) {
      throw new Error('Update returned null - API may have failed');
    }
    
    return result;
  } catch (error) {
    console.error('❌❌❌ dataManager: Error updating card:', error);
    console.error('❌ dataManager: Error message:', error.message);
    console.error('❌ dataManager: Error response:', error.response?.data);
    throw error; // Re-throw instead of returning null
  }
};

export const deleteDigitalCard = async (id) => {
  try {
    await apiService.deleteDigitalCard(id);
  } catch (error) {
    console.error('Error deleting card:', error);
    throw error;
  }
};

export const getPublishedCards = async () => {
  try {
    return await apiService.getPublishedCards();
  } catch (error) {
    console.error('Error fetching published cards:', error);
    return [];
  }
};

export const publishDigitalCard = async (id) => {
  return await updateDigitalCard(id, { status: 'published' });
};

export const unpublishDigitalCard = async (id) => {
  return await updateDigitalCard(id, { status: 'draft' });
};
export const encodeCardDataToUrl = (cardData) => {
  try {
    // Create lightweight version - EXCLUDE images, they're in database
    const lightweightData = {
      n: cardData.name || "",
      p: cardData.position || "",
      d: cardData.description || "",
      ph: cardData.contact_info?.phone || "",
      w: cardData.contact_info?.whatsapp || "",
      e: cardData.contact_info?.email || "",
      a: cardData.address || "",
      li: cardData.social_media?.linkedin || "",
      ig: cardData.social_media?.instagram || "",
      c: cardData.company_name || "",
      id: cardData.id || ""
      // NO IMAGES - will be loaded from database by ID
    };
    
    // Remove empty fields
    Object.keys(lightweightData).forEach(key => {
      if (lightweightData[key] === "" || lightweightData[key] === null || lightweightData[key] === undefined) {
        delete lightweightData[key];
      }
    });
    
    // Compress text fields
    const compressedData = {};
    Object.entries(lightweightData).forEach(([key, value]) => {
      const compressedValue = String(value)
        .trim()
        .replace(/\s+/g, ' ')
        .replace(/Director/gi, 'Dir')
        .replace(/Manager/gi, 'Mgr')
        .replace(/Assistant/gi, 'Asst');
      
      if (compressedValue) {
        compressedData[key] = compressedValue;
      }
    });
    
    const jsonString = JSON.stringify(compressedData);
    const utf8Bytes = new TextEncoder().encode(jsonString);
    const binaryString = Array.from(utf8Bytes).map(byte => String.fromCharCode(byte)).join('');
    const encodedData = btoa(binaryString).replace(/[=]/g, '');
    
    return encodedData;
  } catch (error) {
    console.error('Error encoding card data:', error);
    return null;
  }
};

export const decodeCardDataFromUrl = async (encodedData) => {
  try {
    const padding = '='.repeat((4 - encodedData.length % 4) % 4);
    const binaryString = atob(encodedData + padding);
    const uint8Array = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      uint8Array[i] = binaryString.charCodeAt(i);
    }
    const jsonString = new TextDecoder().decode(uint8Array);
    
    const decodedData = JSON.parse(jsonString);
    const decompressText = (text) => {
      if (!text) return text;
      return String(text)
        .replace(/\bDir\b/gi, 'DIRECTOR')
        .replace(/\bMgr\b/gi, 'MANAGER')
        .replace(/\bAsst\b/gi, 'ASSISTANT');
    };
    
    const cardId = decodedData.id;
    
    // If we have card ID, load full data from API (including images)
    if (cardId) {
      try {
        // Use apiGet directly without auth for public access
        const { apiGet } = await import('./api');
        const response = await apiGet(`/digital-cards/${cardId}`, false); // false = no auth required
        const fullCard = response.data;
        
        if (fullCard && fullCard.status === 'published') {
          // Use full card data from API (includes images)
          return fullCard;
        }
      } catch (error) {
        console.error('Failed to load full card from API:', error);
        // Fall through to use decoded data only
      }
    }
    
    // Fallback: use only decoded data (no images)
    return {
      id: 'shared_card_' + Date.now(),
      name: decompressText(decodedData.n) || "Digital Card",
      position: decompressText(decodedData.p) || "Professional",
      description: decodedData.d || "",
      contact_info: {
        phone: decodedData.ph || "",
        whatsapp: decodedData.w || "",
        email: decodedData.e || ""
      },
      address: decodedData.a || "",
      social_media: {
        linkedin: decodedData.li || "",
        instagram: decodedData.ig || ""
      },
      profile_image: "/assets/2559.jpg", // Default
      company_logo: "/assets/logo.svg",  // Default
      company_name: decompressText(decodedData.c) || "Company",
      status: 'published'
    };
  } catch (error) {
    console.error('Error decoding card data:', error);
    return null;
  }
};

export const generateShareUrl = async (cardId) => {
  try {
    // Get card data from API
    const card = await getDigitalCardById(cardId);
    if (!card) {
      console.error('Card not found');
      return null;
    }
    
    // Create URL slug - DEFAULT: first + last name (or first name only if single name)
    const createUrlSlug = (text) => {
      const nameParts = text.trim().split(/\s+/).filter(p => p);
      
      // If only one name, use it
      if (nameParts.length === 1) {
        return nameParts[0]
          .toLowerCase()
          .replace(/[áàâäã]/g, 'a')
          .replace(/[éèêë]/g, 'e')
          .replace(/[íìîï]/g, 'i')
          .replace(/[óòôöõ]/g, 'o')
          .replace(/[úùûü]/g, 'u')
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-+|-+$/g, '')
          .substring(0, 30);
      }
      
      // Use first and last name
      const firstName = nameParts[0];
      const lastName = nameParts[nameParts.length - 1];
      
      return `${firstName} ${lastName}`
        .toLowerCase()
        .replace(/[áàâäã]/g, 'a')
        .replace(/[éèêë]/g, 'e')
        .replace(/[íìîï]/g, 'i')
        .replace(/[óòôöõ]/g, 'o')
        .replace(/[úùûü]/g, 'u')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '')
        .substring(0, 30);
    };
    
    // Use custom url_slug from database if available, otherwise generate from first name
    const urlSlug = card.url_slug || createUrlSlug(card.name || 'digital-card');
    const baseUrl = window.location.origin;
    
    const shareUrl = `${baseUrl}/card/${urlSlug}`;
    
    return shareUrl;
    
  } catch (error) {
    console.error('Error generating share URL:', error);
    return null;
  }
};



// Debug and utility functions
export const debugDatabase = async () => {
  try {
    const users = await getUsers();
    const cards = await getDigitalCards();
    console.log('Database state - Users:', users.length, 'Cards:', cards.length);
    return { users, cards };
  } catch (error) {
    console.error('Error debugging database:', error);
    return null;
  }
};

export const resetDatabaseWithAdmin = async () => {
  try {
    return await apiService.resetDatabase();
  } catch (error) {
    console.error('Error resetting database:', error);
    throw error;
  }
};


